import { ScoreboardEntry } from "~App";
import { AssetLoader } from "./AssetLoader";
import { Background } from "./Background";
import { Bullet } from "./Bullet";
import { LocalInterface } from "./LocalInterface";
import { Player } from "./Player";
import { RemoteInterface } from "./RemoteInterface";
import { Renderer } from "./Renderer";
import { L, log, logLevel } from "./log";
import bullet from "~../img/bullet.png";
import { BulletController } from "./BulletController";

export type ScheduledAction = {
  tick: number;
  callback: (engine: Engine) => void;
};

export class Engine {
  static HISTORY_SIZE = 100;
  disconnectHandler: () => void;
  pusher: (text: string, colour?: string) => void;
  setScoreboard: React.Dispatch<React.SetStateAction<ScoreboardEntry[]>>;
  renderer: Renderer;
  background: Background;
  remote: RemoteInterface;
  bullets: BulletController;
  local: LocalInterface;
  tick: number;
  localPlayerId: number;
  players: Player[];

  scheduled: ScheduledAction[];

  constructor(
    roomCode: string,
    pusher: (text: string, colour?: string) => void,
    disconnectHandler: () => void,
    setScoreboard: React.Dispatch<React.SetStateAction<ScoreboardEntry[]>>,
    playerName: string,
    playerColour: string
  ) {
    this.renderer = new Renderer(Background.WIDTH, Background.HEIGHT + 120);
    this.background = new Background();
    this.players = [];
    this.localPlayerId = -1;
    this.remote = new RemoteInterface(this, roomCode, playerName, playerColour);
    this.local = new LocalInterface(this);
    this.bullets = new BulletController(this);
    this.tick = 0;
    this.scheduled = [];
    this.pusher = pusher;
    this.disconnectHandler = disconnectHandler;
    this.setScoreboard = setScoreboard;
  }

  getLocalPlayer() {
    return this.players[this.localPlayerId];
  }

  getUpdateContext = logLevel(L.INFO, () => ({
    bg: this.background,
    localPlayer: this.getLocalPlayer(),
  }));

  async update() {
    const updateContext = this.getUpdateContext();
    this.bullets.update(updateContext);
    this.players.forEach((x) => x.update(updateContext));
  }

  async render() {
    const ctx = this.renderer.ctx;
    this.background.render(this.renderer);

    this.bullets.render(this.renderer);
    await Promise.all(this.players.map((x) => x.render(this.renderer)));

    // HUD
    const player = this.getLocalPlayer();
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, Background.HEIGHT, Background.WIDTH, 120);

    // Weapon name
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(player.weaponType.name, 10, Background.HEIGHT + 30);

    // Power
    ctx.fillText("Power:", 10, Background.HEIGHT + 60);
    ctx.fillStyle = "#7F0000";
    ctx.fillRect(100, Background.HEIGHT + 40, 200, 20);
    ctx.fillStyle = "#FF0000";
    ctx.fillRect(
      100,
      Background.HEIGHT + 40,
      (player.power / Player.MAX_POWER) * 200,
      20
    );

    // Reload
    const bulletImg = await AssetLoader.img(bullet);
    ctx.drawImage(bulletImg, 10, Background.HEIGHT + 80);
    ctx.fillStyle = "#000000";
    const cooldown =
      player.cooldown[player.weaponType.id] / player.weaponType.cooldown;
    ctx.fillRect(
      10 + bulletImg.width * (1 - cooldown),
      Background.HEIGHT + 80,
      bulletImg.width * cooldown,
      bulletImg.height
    );
  }

  async onTick() {
    this.scheduled
      .filter((x) => x.tick === this.tick)
      .forEach((x) => x.callback(this));
    this.scheduled = this.scheduled.filter((x) => x.tick > this.tick);

    await this.update();
    await this.render();

    this.tick++;
  }

  schedule(tick: number, callback: (engine: Engine) => void) {
    this.scheduled.push({ tick, callback });
  }
}
